import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextInput, TextareaInput } from "@/components/formfields";
import { SelectDropdown } from "@/components/formfields/SelectDropdown";
import type { EducationDetail, EducationPayload } from "@/interface/profile";
import { useCreateEducation, useUpdateEducation } from "../hooks/useEducation";
import { useProfileDetail } from "../hooks/useProfile";
import { useInfiniteDegreeList } from "@/pages/master-management/hooks/useDegree";
import type { DegreeDetail } from "@/interface";

const educationSchema = z
  .object({
    degree: z.string().trim().min(1, "Degree is required"),
    course_name: z.string().trim().optional(),
    institution: z
      .string()
      .trim()
      .min(1, "Institution is required")
      .max(255, "Institution cannot exceed 255 characters"),
    location: z
      .string()
      .trim()
      .min(1, "Location is required")
      .max(255, "Location cannot exceed 255 characters"),
    grade_cgpa: z
      .union([z.number(), z.string(), z.undefined(), z.null()])
      .optional(),
    start_year: z
      .union([z.number(), z.string(), z.undefined(), z.null()])
      .optional(),
    end_year: z
      .union([z.number(), z.string(), z.undefined(), z.null()])
      .optional(),
    description: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    const currentYear = new Date().getFullYear();

    if (
      data.start_year !== undefined &&
      data.start_year !== "" &&
      data.start_year !== null
    ) {
      const start = Number(data.start_year);
      if (!isNaN(start) && start > currentYear) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Start year cannot be in the future",
          path: ["start_year"],
        });
      }
    }

    if (
      data.start_year !== undefined &&
      data.start_year !== "" &&
      data.start_year !== null &&
      data.end_year !== undefined &&
      data.end_year !== "" &&
      data.end_year !== null
    ) {
      const start = Number(data.start_year);
      const end = Number(data.end_year);
      if (!isNaN(start) && !isNaN(end) && start > end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Start year must be before end year",
          path: ["start_year"],
        });
      }
    }
  });

export type EducationFormValues = z.infer<typeof educationSchema>;

interface EducationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: EducationDetail | null;
  profileId?: number | string;
}

export const EducationFormModal: React.FC<EducationFormModalProps> = ({
  isOpen,
  onClose,
  editData,
  profileId,
}) => {
  const createMutation = useCreateEducation();
  const updateMutation = useUpdateEducation();
  const { data: profileResponse } = useProfileDetail();
  const [degreeSearch, setDegreeSearch] = useState<string>("");
  const [debouncedDegreeSearch, setDebouncedDegreeSearch] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDegreeSearch(degreeSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [degreeSearch]);

  const {
    data: degreeData,
    isLoading: isDegreesLoading,
    fetchNextPage: fetchNextDegrees,
    hasNextPage: hasNextDegrees,
    isFetchingNextPage: isFetchingNextDegrees,
  } = useInfiniteDegreeList({
    page: 1,
    page_size: 10,
    search: debouncedDegreeSearch || undefined,
  });

  const selfId = profileResponse?.data?.id;
  const targetUserId = profileId ?? selfId ?? editData?.user_profile;

  const degrees: DegreeDetail[] = useMemo(() => {
    if (!degreeData?.pages) return [];
    return degreeData.pages.flatMap(
      (page) =>
        (Array.isArray(page?.data)
          ? page.data
          : page?.data?.results || []) as DegreeDetail[],
    );
  }, [degreeData]);

  const degreeOptions = useMemo(() => {
    const list = [...degrees];
    if (
      editData?.degree &&
      typeof editData.degree === "object" &&
      (editData.degree as { id?: number | string })?.id
    ) {
      const editDeg = editData.degree as {
        id: number | string;
        degree_name?: string;
      };
      if (!list.some((d) => String(d.id) === String(editDeg.id))) {
        list.unshift({
          id: Number(editDeg.id),
          degree_name: editDeg.degree_name || String(editDeg.id),
        } as DegreeDetail);
      }
    }
    return list.map((d) => ({
      label: d.degree_name || String(d.id),
      value: String(d.id),
    }));
  }, [degrees, editData]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<EducationFormValues>({
    resolver: zodResolver(educationSchema),
    defaultValues: {
      degree: "",
      course_name: "",
      institution: "",
      location: "",
      grade_cgpa: "",
      start_year: "",
      end_year: "",
      description: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      setDegreeSearch("");
      setDebouncedDegreeSearch("");
      if (editData) {
        const degVal = editData.degree
          ? typeof editData.degree === "object"
            ? String((editData.degree as { id?: number | string })?.id || "")
            : String(editData.degree)
          : "";

        reset({
          degree: degVal,
          course_name: editData.course_name || "",
          institution: editData.institution || "",
          location: editData.location || "",
          grade_cgpa:
            editData.grade_cgpa !== undefined && editData.grade_cgpa !== null
              ? String(editData.grade_cgpa)
              : "",
          start_year:
            editData.start_year !== undefined && editData.start_year !== null
              ? String(editData.start_year)
              : "",
          end_year:
            editData.end_year !== undefined && editData.end_year !== null
              ? String(editData.end_year)
              : "",
          description: editData.description || "",
        });
      } else {
        reset({
          degree: "",
          course_name: "",
          institution: "",
          location: "",
          grade_cgpa: "",
          start_year: "",
          end_year: "",
          description: "",
        });
      }
    }
  }, [isOpen, editData, reset]);

  const onSubmit = async (values: EducationFormValues) => {
    try {
      const parsedStartYear =
        values.start_year !== undefined &&
          values.start_year !== "" &&
          values.start_year !== null
          ? Number(values.start_year)
          : null;

      const parsedEndYear =
        values.end_year !== undefined &&
          values.end_year !== "" &&
          values.end_year !== null
          ? Number(values.end_year)
          : null;

      const payload: EducationPayload = {
        degree: Number(values.degree),
        institution: values.institution,
        course_name: values.course_name || "",
        location: values.location || "",
        grade_cgpa: values.grade_cgpa ? String(values.grade_cgpa) : null,
        start_year: parsedStartYear,
        end_year: parsedEndYear,
        description: values.description || "",
      };

      if (editData?.id) {
        await updateMutation.mutateAsync({
          id: editData.id,
          payload,
          profileId: targetUserId,
        });
      } else {
        await createMutation.mutateAsync({
          payload,
          profileId: targetUserId,
        });
      }

      onClose();
    } catch {
      // Errors handled in mutation hooks
    }
  };

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle>
            {editData ? "Edit Education" : "Add Education"}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectDropdown
                control={control}
                name="degree"
                label="Degree"
                placeholder={
                  isDegreesLoading ? "Loading degrees..." : "Select Degree"
                }
                options={degreeOptions}
                required
                isSearchable
                onSearch={(query) => setDegreeSearch(query)}
                onMenuScrollToBottom={() => {
                  if (hasNextDegrees && !isFetchingNextDegrees) {
                    fetchNextDegrees();
                  }
                }}
                hasMore={hasNextDegrees}
                isLoadingMore={isFetchingNextDegrees}
              />

              <TextInput
                control={control}
                name="course_name"
                label="Course Name"
                placeholder="e.g. Computer Science & Engineering"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                control={control}
                name="institution"
                label="Institution / University"
                placeholder="e.g. Stanford University"
                required
              />

              <TextInput
                control={control}
                name="location"
                label="Location"
                placeholder="e.g. California, USA"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                control={control}
                name="start_year"
                label="Start Year"
                placeholder="e.g. 2020"
                type="number"
                max={currentYear}
              />
              <TextInput
                control={control}
                name="end_year"
                label="End Year"
                placeholder="e.g. 2024"
                type="number"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                control={control}
                name="grade_cgpa"
                label="Grade / CGPA"
                placeholder="e.g. 8.9 or 80"
              />
            </div>

            <TextareaInput
              control={control}
              name="description"
              label="Description"
              placeholder="Relevant coursework, honors, or activities..."
              rows={4}
            />
          </div>

          <DialogFooter className="p-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="px-4 py-2 text-sm font-bold text-slate-600 hover:text-slate-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
              className="px-5 py-2 text-sm font-bold text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
            >
              {isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
                ? "Saving..."
                : editData
                  ? "Save Changes"
                  : "Add Education"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
